import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState, InfoNote } from "@/components/shared";
import { Rocket, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const Metric = ({ label, base, obs, diff, invert }) => {
  const good = diff == null ? null : invert ? diff < 0 : diff > 0;
  return (
    <div className="rounded-md border p-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm tabular-nums">{base ?? "—"} → <span className="font-semibold">{obs ?? "—"}</span></div>
      {diff != null && (
        <div className={`text-xs font-medium tabular-nums ${good ? "text-emerald-700" : diff === 0 ? "text-muted-foreground" : "text-rose-700"}`}>
          {diff > 0 ? "+" : ""}{diff}
        </div>
      )}
    </div>
  );
};

function DeploymentCard({ d }) {
  const [open, setOpen] = useState(false);
  const [m, setM] = useState(null);

  const loadMeasurement = async () => {
    if (!open) {
      try {
        const { data } = await api.get(`/deployments/${d.id}/measurement`);
        setM(data);
      } catch (e) { toast.error(errMsg(e)); }
    }
    setOpen(!open);
  };

  return (
    <Card className="shadow-none" data-testid="deployment-card">
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Rocket className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">{new Date(d.deployed_at).toLocaleString()}</span>
          <Badge variant="outline" className="h-6 px-2 text-xs capitalize">{d.status}</Badge>
          <Badge variant="outline" className="h-6 px-2 text-xs">{d.provider}</Badge>
          <span className="text-xs text-muted-foreground">approved by {d.approvedBy}</span>
          <Button size="sm" variant="outline" className="ml-auto h-7 text-xs" onClick={loadMeasurement} data-testid="deployment-measurement-toggle">
            Measurement {open ? <ChevronUp className="ml-1 h-3 w-3" /> : <ChevronDown className="ml-1 h-3 w-3" />}
          </Button>
        </div>
        {(d.affected_pages || []).length > 0 && (
          <div className="mt-2 truncate font-mono text-[11px] text-muted-foreground">{d.affected_pages.join(", ")}</div>
        )}
        {open && m && (
          <div className="mt-3 space-y-3">
            <InfoNote>{m.disclaimer}</InfoNote>
            {m.windows.map((w) => (
              <div key={w.days} className="rounded-md border p-3" data-testid={`measurement-window-${w.days}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold">{w.days}-day window</span>
                  <Badge variant="outline" className="text-[10px] capitalize">{w.status}</Badge>
                </div>
                {w.status === "pending" ? (
                  <p className="mt-1 text-xs text-muted-foreground">{w.note}</p>
                ) : (
                  <>
                    <p className="mt-1 text-xs text-muted-foreground">{w.wording} (baseline scaled to window length)</p>
                    <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                      <Metric label="Clicks" base={w.baseline_scaled.clicks} obs={w.observed.clicks} diff={w.difference.clicks} />
                      <Metric label="Impressions" base={w.baseline_scaled.impressions} obs={w.observed.impressions} diff={w.difference.impressions} />
                      <Metric label="CTR" base={w.baseline_scaled.ctr} obs={w.observed.ctr} diff={w.difference.ctr} />
                      <Metric label="Position" base={w.baseline_scaled.position} obs={w.observed.position} diff={w.difference.position} invert />
                      <Metric label="Sessions" base={w.baseline_scaled.sessions} obs={w.observed.sessions} diff={w.difference.sessions} />
                      <Metric label="Conversions" base={w.baseline_scaled.conversions} obs={w.observed.conversions} diff={w.difference.conversions} />
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Deployments() {
  const { activeWebsite } = useApp();
  const [items, setItems] = useState(null);

  useEffect(() => {
    (async () => {
      if (!activeWebsite) { setItems([]); return; }
      try {
        const { data } = await api.get(`/websites/${activeWebsite.id}/deployments`);
        setItems(data);
      } catch (e) { toast.error(errMsg(e)); setItems([]); }
    })();
  }, [activeWebsite]);

  if (items === null) return <Skeleton className="h-64 w-full" />;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold tracking-tight">Deployments</h1>
        <p className="text-xs text-muted-foreground">Every production approval creates a deployment record with a measurement baseline and 7 / 14 / 28-day observation windows.</p>
      </div>
      {items.length === 0 ? (
        <EmptyState icon={Rocket} title="No deployments yet" message="Approve a task for production to create the first deployment record and start measurement." testId="deployments-empty" />
      ) : (
        <div className="space-y-3">{items.map((d) => <DeploymentCard key={d.id} d={d} />)}</div>
      )}
    </div>
  );
}
